import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '@/app/providers/authContext';
import { RollingValue } from '@/components/ui';
import { useCartQuery } from '@/features/cart/queries';
import { formatPrice, toAmount } from '@/features/pricing/money';
// Submodule path, not the `@/features/organization` barrel: that barrel's `OrganizationRoute`
// imports from `@/components/layout`, and this file is part of that same barrel (via `Topbar`) —
// going through either index would close a cycle between the shell and the feature modules.
import { useCurrentEstablishment } from '@/features/organization/queries';

import { profilesForNavigationItem } from './navigation';
import styles from './CartBadge.module.css';

/**
 * Carries the open cart's item count into the shell, so a buyer who wandered off to the catalogue
 * still sees what is waiting for them in the cart without opening it.
 *
 * Hidden for a profile that cannot reach `/carrinho` at all (`profilesForNavigationItem`, the same
 * mapping the sidebar and the route guard read) rather than shown disabled: a backoffice profile
 * asking the cart route for a count would get a 403, and a badge that always fails to load is
 * worse than no badge.
 *
 * The count rolls to its new value and the badge gives a short pulse whenever an item comes in or
 * goes out — feedback for the buyer's most repeated action, kept to 170ms so it never delays them.
 */
export interface CartBadgeProps {
  /**
   * `on-brand`: the redesign's header cart (Figma D1) — white icon with the count on its corner,
   * and "Carrinho" over the open cart's total beside it.
   */
  readonly tone?: 'default' | 'on-brand' | undefined;
}

export function CartBadge({ tone = 'default' }: CartBadgeProps = {}) {
  const { profile } = useAuth();
  const canSeeCart = profile !== null && profilesForNavigationItem('cart').includes(profile);
  const { establishmentId } = useCurrentEstablishment();
  const cart = useCartQuery(canSeeCart ? establishmentId : undefined);
  const onCart = useLocation().pathname.startsWith('/carrinho');
  const count = cart.data?.summary.item_count ?? 0;
  const total = toAmount(cart.data?.summary.total_amount) ?? 0;
  const loaded = cart.data !== undefined;

  // The count pulses when it changes — an item came in or went out — never on the first load. The
  // pulse alternates between two identical animations so a burst restarts it instead of queueing.
  const [seen, setSeen] = useState<{ count: number | null; pulse: 0 | 1 | 2 }>({
    count: null,
    pulse: 0,
  });
  if (loaded && seen.count !== count) {
    setSeen({
      count,
      pulse: seen.count === null ? seen.pulse : seen.pulse === 1 ? 2 : 1,
    });
  }

  if (!canSeeCart) {
    return null;
  }

  const label =
    count > 0 ? `Carrinho, ${String(count)} ${count === 1 ? 'item' : 'itens'}` : 'Carrinho';

  return (
    <Link
      to="/carrinho"
      className={styles.link}
      aria-label={label}
      data-topbar-cart=""
      data-tone={tone === 'on-brand' ? 'on-brand' : undefined}
      aria-current={onCart ? 'page' : undefined}
      viewTransition
    >
      <svg
        className={styles.icon}
        viewBox="0 0 24 24"
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 4h2l2.4 11.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.8L20 8H6.2" />
        <circle cx="9" cy="20" r="1.3" />
        <circle cx="17" cy="20" r="1.3" />
      </svg>
      {tone === 'on-brand' ? (
        <span className={styles.summary}>
          <span className={styles.summaryLabel}>Carrinho</span>
          <RollingValue
            className={styles.summaryTotal}
            text={formatPrice(total, cart.data?.summary.currency)}
            value={total}
          />
        </span>
      ) : (
        <span className={styles.text}>Carrinho</span>
      )}
      {count > 0 ? (
        <span className={styles.count} data-pulse={seen.pulse === 0 ? undefined : seen.pulse}>
          <RollingValue text={String(count)} value={count} />
        </span>
      ) : null}
    </Link>
  );
}
